import type {
  ContactsAnalyticsKeys,
  MessagingAnalyticsKeys,
  CallsAnalyticsKeys,
  PerformanceAnalyticsKeys,
  MetricConfig,
  CreditUsageAnalyticsKeys,
} from 'entities/Analytics/api/types'

export const analyticsExportFormats = ['csv', 'txt'] as const

export type MetricsConfiguration = Record<MessagingAnalyticsKeys, MetricConfig>

export const MESSAGING_METRICS_CONFIG: MetricsConfiguration = {
  total: { color: '#0D61F2', name: 'Total' },
  total_inbound: { color: '#3EB786', name: 'Total Inbound' },
  total_outbound: { color: '#0462A9', name: 'Total Outbound' },
  delivered: { color: '#0D61F2', name: 'Delivered' },
  undelivered: { color: '#068FF9', name: 'Undelivered' },
  carrier_violations: { color: '#83C8FC', name: 'Carrier Violations' },
  optouts: { color: '#F4812A', name: 'Optouts' },
  total_rvm: { color: '#C22AF4', name: 'RVM' },
  delivered_rvm: { color: '#DA7CF8', name: 'Delivered RVM' },
  undelivered_rvm: { color: '#EAB6FB', name: 'Undelivered RVM' },
}

export const MESSAGING_METRIC_COLORS: Record<MessagingAnalyticsKeys, string> = {
  total: MESSAGING_METRICS_CONFIG.total.color,
  total_inbound: MESSAGING_METRICS_CONFIG.total_inbound.color,
  total_outbound: MESSAGING_METRICS_CONFIG.total_outbound.color,
  delivered: MESSAGING_METRICS_CONFIG.delivered.color,
  undelivered: MESSAGING_METRICS_CONFIG.undelivered.color,
  carrier_violations: MESSAGING_METRICS_CONFIG.carrier_violations.color,
  optouts: MESSAGING_METRICS_CONFIG.optouts.color,
  total_rvm: MESSAGING_METRICS_CONFIG.total_rvm.color,
  delivered_rvm: MESSAGING_METRICS_CONFIG.delivered_rvm.color,
  undelivered_rvm: MESSAGING_METRICS_CONFIG.undelivered_rvm.color,
}

export const CONTACTS_METRICS_CONFIG: Record<ContactsAnalyticsKeys, MetricConfig> = {
  new_contacts: { color: '#0D61F2', name: 'New Contacts' },
  opted_out: { color: '#068FF9', name: 'Opted-out' },
}

export const CONTACTS_METRIC_COLORS: Record<ContactsAnalyticsKeys, string> = {
  new_contacts: CONTACTS_METRICS_CONFIG.new_contacts.color,
  opted_out: CONTACTS_METRICS_CONFIG.opted_out.color,
}

export const CALLS_METRICS_CONFIG: Record<CallsAnalyticsKeys, MetricConfig> = {
  total: { color: '#F4812A', name: 'Total calls' },
  avg_duration_forwarded: { color: '#FBD4B6', name: 'Avg. duration forwarded' },

  outbound_calls: { color: 'var(--blue-80)', name: 'Outbound calls' },
  answered_outbound: { color: 'var(--light-blue-80)', name: 'Answered' },
  unanswered_outbound: { color: 'var(--blue-60)', name: 'Unanswered' },
  failed_outbound: { color: 'var(--light-blue-60)', name: 'Failed' },
  busy_outbound: { color: 'var(--light-blue-40)', name: 'Busy' },
  avg_duration_outbound: { color: 'var(--blue-30)', name: 'Avg. duration outbound' },

  inbound_calls: { color: 'var(--green-90)', name: 'Inbound calls' },
  answered_inbound_human: { color: 'var(--green-80)', name: 'Answered by human' },
  answered_inbound_ai: { color: 'var(--green-70)', name: 'Answered by AI' },
  missed_inbound: { color: 'var(--green-60)', name: 'Missed' },
  failed_inbound: { color: 'var(--green-50)', name: 'Failed' },
  busy_inbound: { color: 'var(--green-40)', name: 'Busy' },
  forwarded: { color: 'var(--green-30)', name: 'Forwarded' },
  avg_duration_inbound: { color: 'var(--green-20)', name: 'Avg. duration inbound' },
}

export const CALLS_CHART_METRICS_CONFIG = Object.fromEntries(
  Object.entries(CALLS_METRICS_CONFIG).filter(([key]) => !key.startsWith('avg_duration'))
) as Record<
  Exclude<
    CallsAnalyticsKeys,
    'avg_duration_outbound' | 'avg_duration_inbound' | 'avg_duration_forwarded'
  >,
  MetricConfig
>

export const CALLS_METRIC_COLORS: Record<CallsAnalyticsKeys, string> = {
  total: CALLS_METRICS_CONFIG.total.color,
  outbound_calls: CALLS_METRICS_CONFIG.outbound_calls.color,
  inbound_calls: CALLS_METRICS_CONFIG.inbound_calls.color,
  avg_duration_outbound: CALLS_METRICS_CONFIG.avg_duration_outbound.color,
  answered_outbound: CALLS_METRICS_CONFIG.answered_outbound.color,
  unanswered_outbound: CALLS_METRICS_CONFIG.unanswered_outbound.color,
  busy_outbound: CALLS_METRICS_CONFIG.busy_outbound.color,
  failed_outbound: CALLS_METRICS_CONFIG.failed_outbound.color,
  avg_duration_inbound: CALLS_METRICS_CONFIG.avg_duration_inbound.color,
  answered_inbound_human: CALLS_METRICS_CONFIG.answered_inbound_human.color,
  answered_inbound_ai: CALLS_METRICS_CONFIG.answered_inbound_ai.color,
  missed_inbound: CALLS_METRICS_CONFIG.missed_inbound.color,
  busy_inbound: CALLS_METRICS_CONFIG.busy_inbound.color,
  failed_inbound: CALLS_METRICS_CONFIG.failed_inbound.color,
  forwarded: CALLS_METRICS_CONFIG.forwarded.color,
  avg_duration_forwarded: CALLS_METRICS_CONFIG.avg_duration_forwarded.color,
}

export const PERFORMANCE_METRICS_CONFIG = {
  avg_time_to_first_response: { color: '#0D61F2', name: 'Avg time to first response' },
  avg_time_to_response: { color: '#068FF9', name: 'Avg time to response' },
  avg_time_to_close: { color: '#83C8FC', name: 'Avg time to close' },
}

export const PERFORMANCE_METRIC_KEYS = Object.keys(PERFORMANCE_METRICS_CONFIG).filter(
  (key): key is PerformanceAnalyticsKeys => key in PERFORMANCE_METRICS_CONFIG
)

export const CREDIT_USAGE_METRICS_CONFIG = {
  messages_credits: { color: 'var(--blue-80)', name: 'Messages credits' },
  calls_credits: { color: 'var(--light-blue-80)', name: 'Calls credits' },
  chatbots_credits: { color: 'var(--blue-60)', name: 'Texting agent credits' },
  ai_calling_agent_credits: { color: 'var(--light-blue-60)', name: 'Calling agent credits' },
  transcripts_credits: { color: 'var(--light-blue-40)', name: 'Transcripts credits' },
  rvm_credits: { color: 'var(--blue-30)', name: 'Rvm credits' },
}

export const CREDIT_USAGE_METRIC_COLORS: Record<
  Exclude<CreditUsageAnalyticsKeys, 'total_credits'>,
  string
> = {
  messages_credits: CREDIT_USAGE_METRICS_CONFIG.messages_credits.color,
  calls_credits: CREDIT_USAGE_METRICS_CONFIG.calls_credits.color,
  chatbots_credits: CREDIT_USAGE_METRICS_CONFIG.chatbots_credits.color,
  ai_calling_agent_credits: CREDIT_USAGE_METRICS_CONFIG.ai_calling_agent_credits.color,
  transcripts_credits: CREDIT_USAGE_METRICS_CONFIG.transcripts_credits.color,
  rvm_credits: CREDIT_USAGE_METRICS_CONFIG.rvm_credits.color,
}
